<!-- ============================================================================= -->
<!-- Ella Custom JS - Customize The Style For Layout -->
<!-- ============================================================================= -->

<!-- ============================================================================= -->
<!-- IMPORTANT DISCLAIMER -->
<!-- Please use only JS to style the layout. -->
<!-- ============================================================================= -->

(() => {
  'use strict';

  // Bail early if not homepage
  const body = document.body;
  if (!body || !body.classList.contains('template-index')) return;

  // Inject minimal CSS (kept here so you don't have to touch theme CSS)
  const css = `
  .ytm-overlay{position:fixed;inset:0;display:none;place-items:center;background:rgba(0,0,0,.6);z-index:9999}
  .ytm-overlay[aria-hidden="false"]{display:grid}
  .ytm-dialog{position:relative;width:min(92vw,1000px);aspect-ratio:16/9;background:#000;border-radius:12px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.35)}
  .ytm-frame{position:absolute;inset:0;width:100%;height:100%;border:0}
  .ytm-close{position:absolute;top:.5rem;right:.5rem;height:40px;width:40px;border-radius:999px;border:0;background:rgba(0,0,0,.5);color:#fff;cursor:pointer}
  .ytm-close:focus-visible{outline:2px solid #fff;outline-offset:2px}
  .ytm-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
  .ytm-open{overflow:hidden}
  `;
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);

  // Build modal once, reuse
  const overlay = document.createElement('div');
  overlay.className = 'ytm-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.innerHTML = `
    <div class="ytm-dialog" role="document">
      <button class="ytm-close" type="button" aria-label="Close video">
        <span aria-hidden="true">✕</span><span class="ytm-sr-only">Close</span>
      </button>
    </div>`;
  document.body.appendChild(overlay);

  const dialog = overlay.querySelector('.ytm-dialog');
  const closeBtn = overlay.querySelector('.ytm-close');
  let lastTrigger = null;

  // Utility: parse YouTube URL → { id, start }
  function parseYouTube(urlStr){
    try{
      const url = new URL(urlStr, location.href);
      const host = url.hostname.replace(/^www\./,'');
      let id = '';
      let start = 0;

      // Start time (t or start) → seconds
      const t = url.searchParams.get('t') || url.searchParams.get('start');
      if (t) start = parseTimeToSeconds(t);

      if (host === 'youtu.be'){
        id = url.pathname.slice(1).split('/')[0];
      } else if (host.endsWith('youtube.com')){
        const path = url.pathname;
        if (path.startsWith('/watch')) id = url.searchParams.get('v') || '';
        else if (path.startsWith('/embed/')) id = path.split('/')[2] || '';
        else if (path.startsWith('/shorts/')) id = path.split('/')[2] || '';
      }
      if (!/^[\w-]{11}$/.test(id)) return null;
      return { id, start: Number.isFinite(start) ? start : 0 };
    }catch(e){ return null; }
  }

  function parseTimeToSeconds(t){
    // handles "90", "90s", "1m30s", "2h3m", etc.
    if (/^\d+$/.test(t)) return parseInt(t,10);
    let secs = 0;
    const m = String(t).match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i);
    if (!m) return 0;
    if (m[1]) secs += parseInt(m[1],10)*3600;
    if (m[2]) secs += parseInt(m[2],10)*60;
    if (m[3]) secs += parseInt(m[3],10);
    return secs;
  }

  // Open modal with embed
  function openVideo({id, start}){
    // Build privacy-enhanced embed URL
    const params = new URLSearchParams({
      autoplay: '1',
      rel: '0',
      modestbranding: '1',
      playsinline: '1',
      ...(start ? { start: String(start) } : {})
    });
    const src = `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;

    // Insert iframe
    const old = dialog.querySelector('.ytm-frame');
    if (old) old.remove();
    const iframe = document.createElement('iframe');
    iframe.className = 'ytm-frame';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.title = 'YouTube video';
    iframe.src = src;
    dialog.appendChild(iframe);

    overlay.setAttribute('aria-hidden','false');
    document.documentElement.classList.add('ytm-open');
    // Focus management
    closeBtn.focus();
    trapFocus(true);
  }

  // Close modal + cleanup
  function closeVideo(){
    overlay.setAttribute('aria-hidden','true');
    document.documentElement.classList.remove('ytm-open');
    const iframe = dialog.querySelector('.ytm-frame');
    if (iframe) iframe.remove(); // stops playback
    trapFocus(false);
    if (lastTrigger) lastTrigger.focus?.();
    lastTrigger = null;
  }

  // Focus trap within dialog
  let trapActive = false;
  function trapFocus(enable){
    if (enable && !trapActive){
      trapActive = true;
      document.addEventListener('keydown', onKeyDown, true);
    } else if (!enable && trapActive){
      trapActive = false;
      document.removeEventListener('keydown', onKeyDown, true);
    }
  }
  function onKeyDown(e){
    if (overlay.getAttribute('aria-hidden') === 'true') return;
    if (e.key === 'Escape'){ e.preventDefault(); closeVideo(); return; }
    if (e.key !== 'Tab') return;
    const focusables = dialog.querySelectorAll('a,button,input,select,textarea,[tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0], last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first){ e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last){ e.preventDefault(); first.focus(); }
  }

  // Close interactions
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay || e.target === closeBtn) closeVideo();
  }, { passive:true });

  // Delegate clicks for any YouTube <a> on the homepage
  document.addEventListener('click', (e) => {
    // Respect new-tab/OS shortcuts or non-left clicks
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    // Find nearest anchor
    const a = e.target.closest('a[href]');
    if (!a) return;

    // Optional opt-out: data-no-youtube-modal
    if (a.hasAttribute('data-no-youtube-modal')) return;

    const parsed = parseYouTube(a.getAttribute('href'));
    if (!parsed) return; // Not a YouTube link

    e.preventDefault();
    lastTrigger = a;
    openVideo(parsed);
  }, { capture:true });

  // Optional: support theme editor dynamic reloads by listening once per page; no extra work needed.
})();


/* Hide empty custom text block on product pages
   - Looks for: .moreItem-productText .productView-customText
   - Hides the .moreItem-productText if content is empty/null/whitespace
   - Safe for Shopify Theme Editor (re-checks on section events)
*/
(() => {
  const CONTAINER_SEL = '.moreItem-productText';
  const CONTENT_SEL   = '.productView-customText';

  const isEmptyContent = (el) => {
    if (!el) return true;

    // Text check (treat &nbsp; as space)
    const text = (el.textContent || '').replace(/\u00A0/g, ' ').trim();

    // HTML fallback: strip common “empty” markup noise
    const htmlStripped = (el.innerHTML || '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/<br\s*\/?>/gi, '')
      .replace(/<p>\s*<\/p>/gi, '')
      .trim();

    return text.length === 0 && htmlStripped.length === 0;
  };

  const hideIfEmpty = (root = document) => {
    const blocks = root.querySelectorAll(CONTAINER_SEL);
    blocks.forEach((block) => {
      const contentEl = block.querySelector(CONTENT_SEL);
      if (isEmptyContent(contentEl)) {
        // Use both `hidden` and inline style to be robust against theme CSS
        block.setAttribute('hidden', '');
        block.style.display = 'none';
      } else {
        block.removeAttribute('hidden');
        block.style.display = '';
      }
    });
  };

  const init = () => hideIfEmpty();

  // Run once DOM is ready (works with defer/no-defer)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Re-run in Theme Editor or dynamic section loads
  document.addEventListener('shopify:section:load',   (e) => hideIfEmpty(e.target));
  document.addEventListener('shopify:section:select', (e) => hideIfEmpty(e.target));
  document.addEventListener('shopify:section:deselect', (e) => hideIfEmpty(e.target));
  document.addEventListener('shopify:section:reorder', () => hideIfEmpty());
})();